import { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Modal } from "react-bootstrap"
import Select from "react-select"
import PropTypes from "prop-types"
import {
  generateFollowupIdAction,
  addFollowupAction,
  addNoteAction,
  discardFollowupAction,
  getFollowupListAction,
} from "../../../../../store/actions/ServiceFollowUpAction"
import { getDoctorsAction } from "../../../../../store/actions/DoctorsAction"
import {
  fetchAnswersRequest,
  fetchAnswersSuccess,
  fetchAnswersFailure,
} from "../../../../../store/actions/QuestionnaireActions"
import { getAnswerList } from "../../../../../services/QuestionnaireService"
import Swal from "sweetalert2"
import { useRef } from "react"
import { FaMicrophone, FaFile, FaFilePdf, FaFileImage, FaFileAlt, FaExternalLinkAlt } from "react-icons/fa"

const FollowUpForm = ({
  showFollowUpForm,
  setShowFollowUpForm,
  followupOptions,
  CustomOption,
  CustomSingleValue,
  prescriptionId,
  patientId,
}) => {
  const dispatch = useDispatch()
  const [questionNotes, setQuestionNotes] = useState({})

  const {
    data: answers,
    loading,
    error,
  } = useSelector((state) => {
    console.log("State in Questionnaire:", state)
    return state?.questionnaire?.answers || { data: [], loading: false, error: null }
  })

  const [isScheduledFollowup, setIsScheduledFollowup] = useState(false)
  const [selectedScheduleReason, setSelectedScheduleReason] = useState(null)
  const [showDoctorSelect, setShowDoctorSelect] = useState({})
  const [selectedDoctorId, setSelectedDoctorId] = useState(null)
  const [showNotesModal, setShowNotesModal] = useState(false)
  const [noteText, setNoteText] = useState("")
  const [isImportant, setIsImportant] = useState(false)
  const [isEmergency, setIsEmergency] = useState(false)
  const [selectedDoctorForNote, setSelectedDoctorForNote] = useState(null)
  const [selectedDoctors, setSelectedDoctors] = useState({})
  const [questionFilter, setQuestionFilter] = useState("default")
  const [questionData, setQuestionData] = useState([])

  const importantQuestions = useSelector((state) => state?.serviceFollowup?.importantQuestions || [])
  const [isInitialized, setIsInitialized] = useState(false)
  const doctors = useSelector((state) => state?.doctors?.doctors || [])
  const followupIdData = useSelector((state) => state?.serviceFollowup?.followupIdData || { followup_id: "mock_123" })
  const addNoteLoading = useSelector((state) => state?.serviceFollowup?.addNoteLoading || false)
  const notes = useSelector((state) => state?.serviceFollowup?.notes || [])

  const standaloneQuestions = answers.filter((item) => item.isGroup === "No")
  const groupedQuestions = answers.filter((item) => item.isGroup === "Yes")

  const recognitionRef = useRef(null)

  const [activeMic, setActiveMic] = useState(null)
  // activeMic = question_answer_id | "modalNote" | null

  const [speechLang, setSpeechLang] = useState("en-IN")

  const toggleSpeechLang = () => {
    setSpeechLang((prev) => (prev === "en-IN" ? "mr-IN" : "en-IN"))
  }

  const startListening = (key) => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  
    if (!SpeechRecognition) {
      alert("Speech recognition not supported");
      return;
    }
  
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
  
    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
  
    recognition.lang = speechLang;
    recognition.continuous = false;
    recognition.interimResults = false;
  
    setActiveMic(key);
  
    recognition.onresult = (event) => {
      const text = event.results[0][0].transcript;
  
      if (key === "modalNote") {
        setNoteText((prev) => (prev ? prev + " " + text : text));
      } else {
        setQuestionData((prevData) =>
          prevData.map((item) =>
            item.question_answer_id === key
              ? { ...item, notes: item.notes ? item.notes + " " + text : text }
              : item
          )
        );
      }
    };
  
    recognition.onend = () => {
      recognitionRef.current = null;
      setActiveMic(null);
    };
  
    recognition.onerror = () => {
      recognitionRef.current = null;
      setActiveMic(null);
    };
  
    recognition.start();
  };
  
  // Helper function to check if a string is a valid URL
  const isValidUrl = (string) => {
    try {
      const url = new URL(string);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch (_) {
      return false;
    }
  };

  // Helper function to check if string is base64 data URL
  const isDataUrl = (string) => {
    return string && (string.startsWith('data:application/pdf') || 
                     string.startsWith('data:image/') ||
                     string.startsWith('data:application/'));
  };

  // Helper function to get file icon based on URL/file type
  const getFileIcon = (url) => {
    if (url && (url.includes(".pdf") || url.includes("application/pdf"))) {
      return <FaFilePdf className="text-danger me-2" size={18} />;
    }
    if (url && (url.includes(".jpg") || url.includes(".jpeg") || url.includes(".png") || url.includes(".gif") || url.includes(".webp"))) {
      return <FaFileImage className="text-success me-2" size={18} />;
    }
    if (url && (url.includes(".doc") || url.includes(".docx"))) {
      return <FaFileAlt className="text-primary me-2" size={18} />;
    }
    return <FaFile className="text-primary me-2" size={18} />;
  };

  // Helper function to get file name from URL
  const getFileName = (url) => {
    try {
      if (isDataUrl(url)) {
        if (url.includes('application/pdf')) return 'Document.pdf';
        if (url.includes('image/png')) return 'Image.png';
        if (url.includes('image/jpeg')) return 'Image.jpg';
        if (url.includes('image/jpg')) return 'Image.jpg';
        return 'File';
      }
      
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const fileName = pathname.split("/").pop();
      if (fileName && fileName.length > 0) {
        return decodeURIComponent(fileName).substring(0, 50);
      }
      return "View File";
    } catch {
      return "View File";
    }
  };

  // Function to render answer with file handling
  const renderAnswer = (item) => {
    if (!item.answer) return <p className="fs-15 mb-0 font-weight-bold text-muted">No answer</p>;
    
    // Check if it's a file input question with URL or data URL
    if (item.question_type === "File Input" && (isValidUrl(item.answer) || isDataUrl(item.answer))) {
      return (
        <div className="mb-2">
          <a 
            href={item.answer} 
            target="_blank" 
            rel="noopener noreferrer"
            className="d-inline-flex align-items-center text-decoration-none p-2 border rounded bg-light"
            style={{ cursor: "pointer", transition: "all 0.2s" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#e9ecef";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#f8f9fa";
            }}
          >
            {getFileIcon(item.answer)}
            <span className="me-2">{getFileName(item.answer)}</span>
            <FaExternalLinkAlt size={12} className="text-muted" />
          </a>
        </div>
      );
    }
    
    // For other question types, show regular answer
    return <p className="fs-15 mb-0 font-weight-bold">{item.answer}</p>;
  };

  useEffect(() => {
    console.log("Component mounted, fetching answers for patientId:", patientId)
    if (patientId) {
      console.log("Fetching regular answers for patientId:", patientId)
      dispatch(
        fetchAnswers({
          patient_id: patientId,
          is_important: "No",
        }),
      )
    }
  }, [dispatch, patientId])

  useEffect(() => {
    dispatch(getDoctorsAction())
  }, [dispatch])

  useEffect(() => {
    console.log("Answers:", answers)
    if (((answers && answers.length > 0) || importantQuestions.length > 0) && !isInitialized && !loading) {
      try {
        if (answers && answers.length > 0) {
          standaloneQuestions.forEach((answer) => {
            setQuestionData((prevData) => [
              ...prevData,
              {
                question_answer_id: answer.question_answer_id,
                notes: "",
                isImportant: false,
                isEmergency: false,
                doctor_id: null,
              },
            ])
          })

          groupedQuestions.forEach((group) => {
            if (group.questions) {
              group.questions.forEach((question) => {
                setQuestionData((prevData) => [
                  ...prevData,
                  {
                    question_answer_id: question.question_answer_id,
                    notes: "",
                    isImportant: false,
                    isEmergency: false,
                    doctor_id: null,
                  },
                ])
              })
            }
          })
        }
        setIsInitialized(true)
      } catch (error) {
        console.error("Error initializing questions:", error)
      }
    }
  }, [answers, importantQuestions, loading, isInitialized])

  useEffect(() => {
    if (showFollowUpForm) {
      setIsScheduledFollowup(false)
      setSelectedScheduleReason(null)
      setShowDoctorSelect({})
      setSelectedDoctorId(null)
      setNoteText("")
      setIsImportant(false)
      setIsEmergency(false)
      setSelectedDoctorForNote(null)
      dispatch({ type: "RESET_NOTES" })
    }
  }, [showFollowUpForm, dispatch])

  useEffect(() => {
    if (showFollowUpForm && prescriptionId && patientId) {
      const generateFollowupId = async () => {
        try {
          const response = await dispatch(
            generateFollowupIdAction({
              prescription_id: prescriptionId,
              patient_id: patientId,
            }),
          )
          console.log("response", response)
          if (response.status !== "success") {
            throw new Error(response.message || "Failed to generate followup ID")
          }
        } catch (error) {
          console.error("Error generating followup ID:", error)
          Swal.fire({
            title: "Error",
            text: error.message || "Failed to generate followup ID",
            icon: "error",
            confirmButtonText: "OK",
          })
          setShowFollowUpForm(false)
        }
      }

      generateFollowupId()
    }
  }, [showFollowUpForm, prescriptionId, patientId, dispatch, setShowFollowUpForm])

  const applyQuestionFilter = (q) => {
    if (questionFilter === "default") {
      return q.is_negative === "Yes" || q.is_important === "Yes";
    }
    if (questionFilter === "negative") {
      return q.is_negative === "Yes";
    }
    if (questionFilter === "important") {
      return q.is_important === "Yes";
    }
    return true; // ALL
  };

  const getQuestionStyle = (item) => {
    if (item.is_negative === "Yes") {
      return {
        backgroundColor: "#ffe6e6",   // Light red background
        borderLeft: "4px solid #ff4d4d", // Strong red indicator
      };
    }
    return {};
  };

  const handleDoctorChange = (questionId, selectedOption) => {
    setSelectedDoctors((prev) => ({
      ...prev,
      [questionId]: selectedOption
        ? {
          value: selectedOption.value,
          label: selectedOption.label,
          specialization: selectedOption.specialization,
        }
        : null,
    }))

    setQuestionData((prevData) =>
      prevData.map((item) =>
        item.question_answer_id === questionId
          ? { ...item, doctor_id: selectedOption ? selectedOption.value : null }
          : item,
      ),
    )
  }

  const handleCheckboxChange = (questionAnswerId, isChecked) => {
    setQuestionData((prevData) =>
      prevData.map((item) =>
        item.question_answer_id === questionAnswerId ? { ...item, isEmergency: isChecked } : item,
      ),
    )

    setShowDoctorSelect((prev) => ({
      ...prev,
      [questionAnswerId]: isChecked,
    }))
  }

  const handleNotesChange = (qIndex, value) => {
    setQuestionData((prevData) => {
      const newData = [...prevData]
      const questionItem = newData.find((item) => item.question_answer_id === qIndex)
      if (questionItem) {
        questionItem.notes = value
      }
      return newData
    })
  }

  const handleSaveNote = async () => {
    if (!noteText.trim()) {
      Swal.fire({
        title: "Error",
        text: "Please enter note description",
        icon: "warning",
        confirmButtonText: "OK",
      })
      return
    }

    try {
      const admin = JSON.parse(localStorage.getItem("loginDetails")) || {}
      const payload = {
        patient_id: patientId,
        note_description: noteText,
        followup_id: followupIdData?.followup_id || "0",
        prescription_id: prescriptionId,
        is_important: isImportant ? "Yes" : "No",
        is_emergency: isEmergency ? "Yes" : "No",
        doctor_id: selectedDoctorForNote?.value || "0",
        created_by_name: admin.name || "Unknown User",
        profile_image: admin.profile_image || "https://opdcrm-stage.csnsindia.com/public/uploads/UserIcon.png",
      }

      console.log("Sending note payload:", payload)
      const response = await dispatch(addNoteAction(payload))
      console.log("Note response:", response)

      if (response.status === "success") {
        setNoteText("")
        setIsImportant(false)
        setIsEmergency(false)
        setSelectedDoctorForNote(null)
        setShowNotesModal(false)

        Swal.fire({
          title: "Success",
          text: response.message || "Note added successfully",
          icon: "success",
          confirmButtonText: "OK",
        })
      }
    } catch (error) {
      console.error("Error adding note:", error)
      Swal.fire({
        title: "Error",
        text: error.message || "Failed to add note",
        icon: "error",
        confirmButtonText: "OK",
      })
    }
  }

  const handleSubmit = async () => {
    if (!followupIdData?.followup_id) {
      Swal.fire({
        title: "Error",
        text: "Followup ID not generated. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      })
      return
    }

    const followupForm = questionData
      .filter((q) => q.notes.trim() !== "")
      .map((q) => ({
        question_answer_id: q.question_answer_id,
        note_description: q.notes.trim(),
        is_important: q.isImportant ? "Yes" : "No",
        is_emergency: q.isEmergency ? "Yes" : "No",
        doctor_id: q.doctor_id || null,
      }))

    console.log(
      "Doctor ID and Emergency status for each question:",
      followupForm.map((q) => ({
        question_answer_id: q.question_answer_id,
        doctor_id: q.doctor_id,
        is_emergency: q.is_emergency,
      })),
    )
    console.log("Complete Followup Form Data:", followupForm)

    if (followupForm.length === 0) {
      Swal.fire({
        title: "Error",
        text: "Please add at least one note before submitting.",
        icon: "warning",
        confirmButtonText: "OK",
      })
      return
    }

    const payload = {
      followup_id: followupIdData.followup_id,
      prescription_id: prescriptionId,
      patient_id: patientId,
      doctor_id: selectedDoctorId || "0",
      is_intermediate: isScheduledFollowup ? "No" : "Yes",
      schedule_id: selectedScheduleReason?.value || "0",
      followup_form: JSON.stringify(followupForm),
    }

    console.log("Submitting Followup Payload:", payload)

    try {
      const response = await dispatch(addFollowupAction(payload))
      console.log("Add Followup Response:", response)

      if (response.status === "success" || response.success) {
        Swal.fire({
          title: "Success",
          text: "Followup added successfully",
          icon: "success",
          confirmButtonText: "OK",
        })

        dispatch(getFollowupListAction(prescriptionId))
        dispatch({ type: "RESET_NOTES" })
        setQuestionData([])
        setNoteText("")
        setIsScheduledFollowup(false)
        setSelectedScheduleReason(null)
        setShowDoctorSelect({})
        setSelectedDoctorId(null)
        setShowFollowUpForm(false)
      } else {
        throw new Error(response.message || "Failed to add followup")
      }
    } catch (error) {
      console.error("Error adding followup:", error)
      Swal.fire({
        title: "Error",
        text: error.message || "Failed to add followup",
        icon: "error",
        confirmButtonText: "OK",
      })
    }
  }

  // const handleDiscard = async () => {
  //   try {
  //     if (followupIdData?.followup_id) {
  //       await dispatch(discardFollowupAction(followupIdData.followup_id))
  //     }
  //     setIsScheduledFollowup(false)
  //     setSelectedScheduleReason(null)
  //     setShowDoctorSelect({})
  //     setSelectedDoctorId(null)
  //     setShowNotesModal(false)
  //   } catch (error) {
  //     console.error("Error discarding followup:", error)
  //     Swal.fire({
  //       title: "Error",
  //       text: error.message || "Failed to discard followup",
  //       icon: "error",
  //       confirmButtonText: "OK",
  //     })
  //   }
  // }
  const handleDiscard = async () => {
    try {
      if (followupIdData?.followup_id) {
        await dispatch(
          discardFollowupAction(
            followupIdData.followup_id
          )
        )
      }
  
      // reset states
      setIsScheduledFollowup(false)
      setSelectedScheduleReason(null)
      setShowDoctorSelect({})
      setSelectedDoctorId(null)
      setShowNotesModal(false)
  
      // clear form
      setQuestionData([])
      setNoteText("")
  
      // hide followup form
      setShowFollowUpForm(false)
  
    } catch (error) {
      console.error(
        "Error discarding followup:",
        error
      )
  
      Swal.fire({
        title: "Error",
        text:
          error.message ||
          "Failed to discard followup",
        icon: "error",
        confirmButtonText: "OK",
      })
    }
  }

  const selectedDoctor = doctors.find((doc) => doc.doctor_id === selectedDoctorId)
  const selectedDoctorValue = selectedDoctor
    ? {
      value: selectedDoctor.doctor_id,
      label: selectedDoctor.doctor_name,
      specialization: selectedDoctor.specialization,
    }
    : null

  const formatOptionLabel = ({ label, specialization }) => (
    <div>
      <div style={{ fontWeight: "bold" }}>{label}</div>
      <div style={{ fontSize: "0.8em", color: "#666" }}>{specialization}</div>
    </div>
  )

  if (!showFollowUpForm) return null

  if (followupIdData?.followupIdLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  if (followupIdData?.followupIdError) {
    return (
      <div className="alert alert-danger" role="alert">
        Failed to generate followup ID. Please try again.
      </div>
    )
  }

  const renderQuestion = (item) => {
    const questionDataItem = questionData.find(
      (q) => q.question_answer_id === item.question_answer_id
    );
  
    return (
      <div key={item.question_answer_id} className="col-xl-12 mb-4">
        <div
          className="timeline-panel bgl-dark border-0 p-4 rounded"
          style={getQuestionStyle(item)}
        >
          <div className="row align-items-start">
  
            {/* 🔹 LEFT: Question + Answer */}
            <div className="col-md-4">
              <h6 className="font-weight-bold mb-2">
                {item.question || "No question text"}
              </h6>
              {renderAnswer(item)}
            </div>
  
            {/* 🔹 CENTER: Notes (FIXED PROPERLY) */}
            <div className="col-md-4 position-relative">

<textarea
  className="form-control"
  placeholder="Enter Notes here.."
  value={questionDataItem?.notes || ""}
  onChange={(e) =>
    handleNotesChange(item.question_answer_id, e.target.value)
  }
  style={{
    height: "38px",          // ✅ fixed height
    paddingRight: "80px",    // ✅ space for icons
  }}
/>

{/* 🌐 LANG */}
<button
  type="button"
  onClick={toggleSpeechLang}
  style={{
    position: "absolute",
    right: "50px",
    top: "50%",
    transform: "translateY(-50%)",
    padding: "2px 6px",
    borderRadius: "8px",
    fontSize: "11px",
    border: "1px solid #ccc",
    background: "#fff",
  }}
>
  {speechLang === "en-IN" ? "EN" : "MR"}
</button>

{/* 🎙 MIC */}
<button
  type="button"
  onClick={() => startListening(item.question_answer_id)}
  style={{
    position: "absolute",
    right: "16px",
    top: "50%",
    transform: "translateY(-50%)",
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "none",
    background:
      activeMic === item.question_answer_id
        ? "#ff4d4d"
        : "#f1f3f5",
  }}
>
  <FaMicrophone
    style={{
      color:
        activeMic === item.question_answer_id
          ? "white"
          : "#333",
      fontSize: "12px",
    }}
  />
</button>

</div>
  
            {/* 🔹 RIGHT: Emergency + Doctor */}
            <div className="col-md-4">
  
              {/* Emergency */}
              <div className="form-check mb-2 d-flex align-items-center">
                <input
                  className="form-check-input me-2"
                  type="checkbox"
                  id={`emergencyConsulting-${item.question_answer_id}`}
                  checked={questionDataItem?.isEmergency || false}
                  onChange={(e) =>
                    handleCheckboxChange(
                      item.question_answer_id,
                      e.target.checked
                    )
                  }
                />
                <label
                  className="form-check-label mb-0"
                  htmlFor={`emergencyConsulting-${item.question_answer_id}`}
                >
                  Emergency
                </label>
              </div>
  
              {/* Doctor Dropdown */}
              {showDoctorSelect[item.question_answer_id] && (
                <Select
                  value={selectedDoctors[item.question_answer_id] || null}
                  onChange={(selectedOption) =>
                    handleDoctorChange(
                      item.question_answer_id,
                      selectedOption
                    )
                  }
                  options={doctors.map((doctor) => ({
                    value: doctor.doctor_id,
                    label: doctor.doctor_name,
                    specialization: doctor.specialization,
                  }))}
                  formatOptionLabel={formatOptionLabel}
                  isClearable
                  isSearchable
                  placeholder="Select Doctor"
                />
              )}
  
            </div>
  
          </div>
        </div>
      </div>
    );
  };
  
  const filterOptions = [
    { value: "default", label: "Negative + Important" },
    { value: "negative", label: "Negative" },
    { value: "important", label: "Important" },
    { value: "all", label: "All" },
  ];

  const renderGroup = (group) => {
    const filteredQuestions = group.questions.filter(applyQuestionFilter)

    if (filteredQuestions.length === 0) {
      return null
    }

    return (
      <div key={group.group_id} className="col-xl-12 mb-4">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">{group.group_name}</h4>
          </div>
          <div className="card-body">
            <div className="row">{filteredQuestions.map((question) => renderQuestion(question))}</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mb-4 border rounded p-3 shadow-sm bg-white mt-4">
      <div className="row align-items-center mb-3">
        <div className="col-md-6 d-flex align-items-center gap-3">
          <div className="form-check form-switch m-0">
            <input
              className="form-check-input"
              type="checkbox"
              id="followupTypeSwitch"
              checked={isScheduledFollowup}
              onChange={() => setIsScheduledFollowup(!isScheduledFollowup)}
            />
          </div>
          <label
            className="form-check-label mb-0"
            htmlFor="followupTypeSwitch"
            style={{ minWidth: "180px", whiteSpace: "nowrap" }}
          >
            {isScheduledFollowup ? "Scheduled Followup" : "Intermediate Followup"}
          </label>

          {isScheduledFollowup && (
            <div style={{ flex: 1 }}>
              <Select
                value={selectedScheduleReason}
                onChange={setSelectedScheduleReason}
                options={followupOptions}
                isClearable
                placeholder="Select Schedule Follow-Up"
                className="react-select-container"
                classNamePrefix="react-select"
                components={{
                  Option: CustomOption,
                  SingleValue: CustomSingleValue,
                }}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: "50px",
                    height: "50px",
                  }),
                  singleValue: (provided) => ({
                    ...provided,
                    lineHeight: "50px",
                  }),
                  dropdownIndicator: (provided) => ({
                    ...provided,
                    padding: "4px",
                  }),
                  indicatorSeparator: (provided) => ({
                    ...provided,
                    display: "none",
                  }),
                }}
              />
            </div>
          )}
        </div>
        <div className="col-md-6 d-flex justify-content-end">
          <Select
            value={filterOptions.find((opt) => opt.value === questionFilter)}
            onChange={(opt) => setQuestionFilter(opt.value)}
            options={filterOptions}
            isSearchable={false}
            className="basic-single"
            classNamePrefix="select"
            styles={{
              control: (provided) => ({
                ...provided,
                minHeight: "40px",
                height: "40px",
                width: "250px",
              }),
            }}
          />
        </div>
      </div>

      <h5 className="mb-3">Follow-up Form</h5>

      {standaloneQuestions.length > 0 && (
        <div className="mb-4">
          <div className="row">
            {standaloneQuestions
              .filter(applyQuestionFilter)
              .map(renderQuestion)}
          </div>
        </div>
      )}

      {groupedQuestions.length > 0 && (
        <div className="mb-4">
          <div className="row">{groupedQuestions.map(renderGroup).filter((group) => group !== null)}</div>
        </div>
      )}

      {notes.length > 0 && (
        <div className="mt-4">
          {notes.map((note, index) => (
            <div
              key={index}
              className="mb-2 custom-timeline-panel timeline-panel bgl-dark flex-wrap border-0 p-3 rounded"
            >
              <h6 className="font-weight-bold">{note?.note_description}</h6>
              <p className="fs-15 mb-0">Date: {note?.created_at}</p>
              {note?.is_important === "Yes" && <span className="badge bg-warning me-1">Important</span>}
              {note?.is_emergency === "Yes" && <span className="badge bg-danger">Emergency</span>}
            </div>
          ))}
        </div>
      )}

      <div className="d-flex justify-content-end gap-3 mt-4">
        <button
          type="button"
          className="btn btn-sm btn-outline-primary px-4 py-2"
          onClick={() => setShowNotesModal(true)}
        >
          Add Notes
        </button>
        <button
          type="button"
          className="btn btn-outline-danger px-4 py-2"
          onClick={handleDiscard}
          disabled={addNoteLoading}
        >
          Discard
        </button>
        <button type="submit" className="btn btn-primary px-4 py-2" onClick={handleSubmit} disabled={addNoteLoading}>
          {addNoteLoading ? (
            <>
              <i className="fas fa-spinner fa-spin me-2"></i>
              Adding Follow-Up...
            </>
          ) : (
            "Save"
          )}
        </button>
      </div>

      {showNotesModal && (
        <Modal
          show={showNotesModal}
          onHide={() => setShowNotesModal(false)}
          backdrop="static"
          keyboard={false}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Add Notes</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3 position-relative">
              <label className="form-label">Note</label>

              <textarea
                className="form-control pe-5"
                rows="3"
                value={noteText}
                placeholder="Enter the Notes here"
                onChange={(e) => setNoteText(e.target.value)}
              />

              {/* LANG */}
              <button
                type="button"
                onClick={toggleSpeechLang}
                style={{
                  position: "absolute",
                  right: "44px",
                  top: "75px",
                  padding: "2px 6px",
                  borderRadius: "10px",
                  fontSize: "11px",
                  border: "1px solid #ccc",
                  background: "#fff",
                }}
              >
                {speechLang === "en-IN" ? "EN" : "MR"}
              </button>

              {/* MIC */}
              <button
                type="button"
                onClick={() => startListening("modalNote")}
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "70px",
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  border: "none",
                  background:
                    activeMic === "modalNote" ? "#ff4d4d" : "#f1f3f5",
                  boxShadow:
                    activeMic === "modalNote"
                      ? "0 0 8px rgba(255,0,0,0.6)"
                      : "0 2px 4px rgba(0,0,0,0.2)",
                }}
              >
                <FaMicrophone
                  style={{
                    color: activeMic === "modalNote" ? "white" : "#333",
                    fontSize: "13px",
                  }}
                />
              </button>
            </div>

            <div className="form-check d-flex align-items-center mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="importantCheck"
                checked={isImportant}
                onChange={(e) => setIsImportant(e.target.checked)}
              />
              <label className="form-check-label ms-2" htmlFor="importantCheck">
                Important
              </label>
            </div>
            <div className="form-check d-flex align-items-center mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="emergencyCheck"
                checked={isEmergency}
                onChange={(e) => setIsEmergency(e.target.checked)}
              />
              <label className="form-check-label ms-2" htmlFor="emergencyCheck">
                Emergency
              </label>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-outline-danger" onClick={() => setShowNotesModal(false)}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSaveNote} disabled={addNoteLoading}>
              {addNoteLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin me-2"></i>
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  )
}

FollowUpForm.propTypes = {
  showFollowUpForm: PropTypes.bool.isRequired,
  setShowFollowUpForm: PropTypes.func.isRequired,
  followupOptions: PropTypes.array.isRequired,
  CustomOption: PropTypes.func.isRequired,
  CustomSingleValue: PropTypes.func.isRequired,
  prescriptionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
}

export default FollowUpForm

export const fetchAnswers = (payload) => {
  return async (dispatch) => {
    dispatch(fetchAnswersRequest())
    console.log("Fetching answers with payload:", payload)
    try {
      const response = await getAnswerList(payload)
      console.log("Fetched answers:", response.data)
      dispatch(fetchAnswersSuccess(response.data.data))
    } catch (error) {
      dispatch(fetchAnswersFailure(error.message))
    }
  }
}